import { Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { Emblem, Emblem40 } from './Emblem';
import { Logo } from './Logo';
import { NRTLogo } from './NRTLogo';
import { NRTLogoBlack } from './NRTLogoBlack';

const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

const styles = StyleSheet.create({
  page: {
    paddingTop: 110,
    paddingBottom: 45,
    paddingHorizontal: 50,
    fontSize: 12,
    fontFamily: 'Inter',
    flex: 1,
    flexDirection: 'column',
  },
  emblemContainer: {
    position: 'absolute',
    left: 0,
    top: 60,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.1,
  },
  outerBorder: {
    position: 'absolute',
    top: 22,
    bottom: 22,
    left: 22,
    right: 22,
    zIndex: -1,
    borderWidth: 4,
    borderColor: '#4532AD',
  },
  innerBorder: {
    position: 'absolute',
    top: 32,
    bottom: 32,
    left: 32,
    right: 32,
    zIndex: -1,
    borderWidth: 1,
    borderColor: '#4532AD',
  },
  title: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 20,
    fontFamily: 'Inter-SemiBold',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  label: {
    fontSize: 10,
    flexShrink: 1,
    marginRight: 4,
    lineHeight: 1.5,
  },
  value: {
    fontSize: 11,
    flexShrink: 1,
    textAlign: 'left',
    flexGrow: 1,
    fontWeight: 'bold',
    fontFamily: 'Inter-SemiBold',
    lineHeight: 1.5,
    color: '#11181C',
  },
  signatureContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: 2,
    fontSize: 10,
    width: '160px',
    textAlign: 'left',
  },
  dukeFooterContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    fontSize: 10,
  },
  nationallyRecognisedTrainingLogo: {
    position: 'relative',
    width: '78px',
    height: '78px',
  },
  australianQualificationsFrameworkLogo: {
    position: 'relative',
    width: '130px',
    height: '74px',
  },
  signatureImage: {
    width: 80,
    height: 30,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 0,
    fontSize: 10,
    textAlign: 'left',
  },
  message: {
    fontSize: 10,
    lineHeight: 1.5,
    marginTop: '32px',
  },
  header: {
    position: 'absolute',
    top: 50,
    left: 50,
    right: 50,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

function PdfLayout({ children, title, paginate = false }): React.JSX.Element {
  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.outerBorder} fixed={true} />
      <View style={styles.innerBorder} fixed={true} />
      <View style={styles.emblemContainer} fixed={true}>
        <Emblem />
      </View>
      <View style={styles.header} fixed={true}>
        <Logo />
      </View>
      {paginate ? (
        <>
          <Text style={styles.title}>{title}</Text>
          {children}
        </>
      ) : (
        <View wrap={true}>
          <Text style={styles.title}>{title}</Text>
          {children}
        </View>
      )}
    </Page>
  );
}

export function Row({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean;
}): React.JSX.Element {
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{typeof value === 'boolean' ? 'Yes' : value}</Text>
    </View>
  );
}

export function SignatureSection({
  signatureDate,
  includeCEOSignature = false,
  style: overridingStyle,
  dateLabel = 'Date',
}: {
  signatureDate?: string;
  includeCEOSignature?: boolean;
  style?: any;
  dateLabel?: string;
}): React.JSX.Element {
  const imgStyles =
    overridingStyle?.textAlign === 'center' ? { marginLeft: 'auto', marginRight: 'auto' } : {};
  return (
    <View style={[styles.signatureContainer, overridingStyle]}>
      {includeCEOSignature ? (
        <>
          <Image
            src={`${baseUrl}/public/images/ceo_signature.png`}
            style={[styles.signatureImage, imgStyles]}
          />
          <Text style={{ fontFamily: 'Inter-SemiBold' }}>Paul Licuria</Text>
          <Text>CEO</Text>
          <Text>Authorised Officer</Text>
        </>
      ) : (
        <>
          <Image
            src={`${baseUrl}/public/images/macleod_signature.png`}
            style={[styles.signatureImage, imgStyles]}
          />
          <Text style={{ fontFamily: 'Inter-SemiBold' }}>Michael Macleod</Text>
          <Text>Customer Experience Specialist</Text>
          <Text>Authorised Officer</Text>
        </>
      )}

      {signatureDate != null ? (
        <Text>
          {dateLabel}: {signatureDate}
        </Text>
      ) : null}
    </View>
  );
}

export function DukeFooterLogo(): React.JSX.Element {
  return (
    <View style={styles.dukeFooterContainer}>
      <Emblem40 />
      <View
        style={{
          display: 'flex',
          fontSize: 10,
          justifyContent: 'space-between',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Text style={{ fontFamily: 'Inter-SemiBold' }}>Duke Education</Text>
        <Text>RTO No. 22497</Text>
        <Text>ABN 46 147 978 596</Text>
        <Text>support@duke.co</Text>
        <Text>1300 385 326</Text>
      </View>
    </View>
  );
}

export function NationallyRecognisedTrainingLogo(): React.JSX.Element {
  return (
    <View style={styles.nationallyRecognisedTrainingLogo}>
      <NRTLogo />
    </View>
  );
}

export function NationallyRecognisedTrainingLogoBlack(): React.JSX.Element {
  return (
    <View>
      <NRTLogoBlack />
    </View>
  );
}

export function AustralianQualificationsFrameworkLogo(): React.JSX.Element {
  return (
    <View style={styles.australianQualificationsFrameworkLogo}>
      <Image src={`${baseUrl}/public/images/aqf-logo.png`} />
    </View>
  );
}

export function Message({ text }: { text: string }): React.JSX.Element {
  return (
    <View style={styles.message}>
      <Text>{text}</Text>
    </View>
  );
}

export default PdfLayout;
